import React from "react";
import type { ChangesWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/changesWorkflow";
import WorkflowReview from "./WorkflowReview";
import { Heading, CodeDisplay, Alert, Tabs, Card, CardBody } from "@aidkitorg/component-library";

type DiffCounts = {
  additions: number;
  deletions: number;
}

export const ansiToReact = (input: string): { segments: React.ReactNode[], counts: DiffCounts } => {
  const segments: React.ReactNode[] = [];
  const counts: DiffCounts = { additions: 0, deletions: 0 };

  // Split input into lines to handle diff-style prefixes
  const lines = input.split('\n');
  
  lines.forEach((line, lineIndex) => {
    let currentColor: string | undefined = undefined;
    let processedLine = line;
    
    // Check if line starts with diff prefixes or ansi escape codes
    if (line.startsWith('-') || line.startsWith('\x1b[31m')) {
      currentColor = 'red';
      counts.deletions++;
    } else if (line.startsWith('+') || line.startsWith('\x1b[32m')) {
      currentColor = 'green';
      counts.additions++;
    }

    segments.push(
      <pre 
        style={{ 
          color: currentColor, 
          margin: 0,
          whiteSpace: 'pre',
          display: 'inline'
        }} 
        className={currentColor === 'red' || currentColor === 'green' ? 'font-semibold' : ''}
        key={`${lineIndex}`}
      >
        {processedLine}
      </pre>
    );
    
    // Add newline except for the last line
    if (lineIndex < lines.length - 1) {
      segments.push(<br key={`br-${lineIndex}`} />);
    }
  });

  return { segments, counts };
};

export function ChangesWorkflowReview({ distroCommitId, surveyHashMismatch, isPendingReview, isApproved, createdBy }: { distroCommitId: string, surveyHashMismatch: boolean, isPendingReview: boolean, isApproved: boolean, createdBy: string }) {
  const renderChangesResults = (workflow: any) => {
    if (!workflow?.result?.success) {
      return null;
    }

    const result = workflow.result as ChangesWorkflowResult;

    // Create tabs for each section that has changes and calculate counts
    const tabs = Object.entries(result)
      .filter(([key, diff]) => key !== 'success' && typeof diff === 'string' && diff.trim() !== '')
      .map(([key, diff]) => {
        const { segments, counts } = ansiToReact(diff as string);
        return {
          name: <div>
            <span className="mr-1">{key.charAt(0).toUpperCase() + key.slice(1)} </span>
            <span className="text-green-600 font-medium">+{counts.additions}</span>
            <span className="text-gray-500 font-medium">/</span>
            <span className="text-red-600 font-medium">-{counts.deletions}</span>
          </div>,
          key,
          counts,
          content: (
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto shadow-sm whitespace-pre font-mono p-2">
              {segments.map((segment, index) => (
                <React.Fragment key={index}>{segment}</React.Fragment>
              ))}
            </div>
          )
        };
      });

    return (
      <div className="space-y-6">
        {surveyHashMismatch && (isPendingReview || isApproved) && (
          <div className="mb-6">
            <p className="text-red-500 font-bold">
              The survey has been saved outside of the review flow. 
              This diff no longer reflects the current state of the survey.
            </p>
          </div>
        )}
        {tabs.length > 0 ? (
          <>
            <div className="mb-6">
              <p className="text-sm text-gray-500 flex items-center gap-1 ml-2">
                {createdBy} is suggesting
                <span>
                  <span className="text-green-600 font-medium">+{tabs.reduce((acc, { counts }) => acc + counts.additions, 0)}</span>
                  <span className="text-gray-500 font-medium">/</span>
                  <span className="text-red-600 font-medium">-{tabs.reduce((acc, { counts }) => acc + counts.deletions, 0)}</span>
                </span>
                changes.
              </p>
            </div>
            <div className="mb-6">
              <Tabs tabs={tabs} justify="left" />
            </div>
          </>
        ) : (
          <Alert
            title="No Diff Available"
            variant="warning"
            className="mb-6"
          >
            No diff available. The proposed survey is identical to the current survey.
          </Alert>
        )}
      </div>
    );
  };

  return (
    <WorkflowReview
      distroCommitId={distroCommitId}
      workflowName="changes"
      title="Survey Changes"
      renderSummary={renderChangesResults}
    />
  );
}
